import classnames from 'classnames';
import DateRangePicker from 'components/DateRangePicker';
import Modal from 'components/Modal';
import RadioButton from 'components/RadioButton';
import RoundedButton from 'components/RoundedButton';
import ChevronIcon from 'components/icons/ChevronIcon';
import { AVAILABILITY_FILTER_OPTIONS } from 'data/subOptions/availabilitySubOptions';
import useIsMobile from 'hooks/useIsMobile';
import React from 'react';
import { useI18n } from '~/stores/i18n';

const CSS = require('./DatePicker.module.scss');

interface DatePickerProps {
  startDate: Date;
  onSelectDay: (date: Date) => void;
  selectedFrom: Date | null;
  selectedTo: Date | null;
  onClose: () => void;
  resetDates: () => void;
  showAvailabilityOptions: { show: boolean; showNow: boolean };
  onClickNow?: (() => void) | null;
  onClickToday: () => void;
  onClickTomorrow: () => void;
}

function DatePicker({
  startDate,
  onSelectDay,
  selectedFrom,
  selectedTo,
  onClose,
  resetDates,
  showAvailabilityOptions,
  onClickToday,
  onClickTomorrow,
  onClickNow,
}: DatePickerProps) {
  const isMobile = useIsMobile();
  const { translate } = useI18n();
  const datePickerContent = (
    <div className={CSS['DatePickerOverlay-content']}>
      <div className={CSS['DatePickerOverlay-topbar']}>
        <button className={CSS['DatePickerOverlay-back']} onClick={onClose}>
          <ChevronIcon width={9} height={15} color="#000" direction="left" />
        </button>
        {!isMobile && (
          <span className={CSS['DatePickerOverlay-modalTitle']}>
            {translate(
              'UserEnvironment.DatePicker.selectDate',
              {},
              { textOnly: true, defaultValue: 'Select date' },
            )}
          </span>
        )}
        <button className={CSS['DatePickerOverlay-reset']} onClick={resetDates}>
          {translate(
            'UserEnvironment.DatePicker.reset',
            {},
            { textOnly: true, defaultValue: 'Reset' },
          )}
        </button>
      </div>
      {isMobile && (
        <span className={CSS['DatePickerOverlay-title']}>
          {translate(
            'UserEnvironment.DatePicker.selectDate',
            {},
            { textOnly: true, defaultValue: 'Select date' },
          )}
        </span>
      )}
      <DateRangePicker
        onClickNow={onClickNow}
        onClickToday={onClickToday}
        onClickTomorrow={onClickTomorrow}
        startDate={startDate}
        onSelectDay={onSelectDay}
        selectedFrom={selectedFrom}
        selectedTo={selectedTo}
      />
      {showAvailabilityOptions.show && (
        <div className={CSS.FiltersContainer}>
          <div className={CSS.Filters}>
            <div className={CSS.Filter} onClick={onClickToday}>
              <RadioButton
                value={AVAILABILITY_FILTER_OPTIONS.TODAY}
                name={'availability'}
                className={CSS.FilterCheckbox}
              />
              <span className={CSS.FilterLabel}>
                {translate(
                  'SearchPage.DateFilter.availabilityToday',
                  {},
                  { textOnly: true, defaultValue: 'Today' },
                )}
              </span>
            </div>
            <div className={CSS.Filter} onClick={onClickTomorrow}>
              <RadioButton
                value={AVAILABILITY_FILTER_OPTIONS.TOMORROW}
                name={'availability'}
                className={CSS.FilterCheckbox}
              />
              <span className={CSS.FilterLabel}>
                {translate(
                  'SearchPage.DateFilter.availabilityTomorrow',
                  {},
                  { textOnly: true, defaultValue: 'Tomorrow' },
                )}
              </span>
            </div>
          </div>
        </div>
      )}
      {
        <div className={classnames(CSS['DatePickerOverlay-SaveHolder'])}>
          <RoundedButton
            fullWidth
            pink
            className={classnames(CSS['DatePickerOverlay-Save'])}
            edgeRounding="medium"
            onClick={onClose}
          >
            {translate(
              'UserEnvironment.DatePicker.save',
              {},
              { textOnly: true, defaultValue: 'Save' },
            )}
          </RoundedButton>
        </div>
      }
    </div>
  );

  return (
    <Modal
      className={CSS['DatePickerModal']}
      show={true}
      onClose={onClose}
      hideClose
      fullscreen={isMobile}
    >
      {datePickerContent}
    </Modal>
  );
}

export default DatePicker;
